import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  NAV_STARS,
  formatDeclination,
  formatDegMin,
  type NavStar,
} from "@nav-games/astro";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface StarIdProps {
  onNavigate: (to: string) => void;
}

const MODULE = "astro";
const GAME = "star-id";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

interface Card {
  star: NavStar;
  choices: readonly string[];
}

function buildCard(): Card {
  const star = NAV_STARS[Math.floor(Math.random() * NAV_STARS.length)]!;
  // Distractors: prefer stars in a similar declination band so the
  // question really turns on identification, not "which one's in the
  // sky right now."
  const pool = NAV_STARS.filter((s) => s.name !== star.name);
  pool.sort((a, b) => Math.abs(a.decDeg - star.decDeg) - Math.abs(b.decDeg - star.decDeg));
  // Pull a few from the near band, then shuffle and take the first
  // three so the choices feel varied not strictly ordered.
  const near = pool.slice(0, 12);
  for (let i = near.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = near[i]!;
    near[i] = near[j]!;
    near[j] = a;
  }
  const distractors = near.slice(0, CHOICES_PER_CARD - 1).map((s) => s.name);
  const choices = [star.name, ...distractors];
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = choices[i]!;
    choices[i] = choices[j]!;
    choices[j] = a;
  }
  return { star, choices };
}

export function StarId({ onNavigate }: StarIdProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === card.star.name;
      const attempt: Omit<Attempt, "module"> = {
        char: card.star.name,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/astro")}
      />
    );
  }

  const revealed = chosen !== null;
  const s = card.star;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "astro" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Name the navigational star</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          The 58 named stars the Nautical Almanac tabulates. Match the
          constellation + position + brightness to the right name.
        </p>

        <div
          style={{
            padding: 12,
            borderRadius: 6,
            background: "var(--bg-elev)",
            marginBottom: 12,
          }}
        >
          <p style={{ margin: 0 }}>
            Constellation: <strong>{s.constellation}</strong>
          </p>
          <p style={{ margin: "4px 0 0" }}>
            Declination: <strong>{formatDeclination(s.decDeg)}</strong>
          </p>
          <p style={{ margin: "4px 0 0" }}>
            SHA: <strong>{formatDegMin(s.shaDeg)}</strong>
          </p>
          <p style={{ margin: "4px 0 0" }}>
            Magnitude: <strong>{s.magnitude.toFixed(1)}</strong>
            <span className="muted small">
              {" "}
              ({s.magnitude < 1 ? "brilliant" : s.magnitude < 2 ? "bright" : "useful at twilight"})
            </span>
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c === s.name;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 17,
                  padding: "14px 6px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === s.name
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === s.name ? `✓ Correct — ${s.name}.` : `✗ Not quite — it's ${s.name}.`}
            </p>
            <p className="small muted" style={{ margin: "6px 0 0" }}>
              {s.name} (mag {s.magnitude.toFixed(1)}) sits in {s.constellation} at
              SHA {formatDegMin(s.shaDeg)}, Dec {formatDeclination(s.decDeg)}.
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next star →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/astro")}>
        ← Back to Astronavigation
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Astronavigation
        </button>
      </div>
    </>
  );
}
